import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, Box, Typography} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUserID, selectFirstName, selectLastName } from '../redux/slice/authSlice';
import { addDoc, collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { toast } from 'react-toastify';
import { analyzeTaskContent } from '../utils/aiAnalysis'; 

const initialFormState = {
  title: '',
  description: '',
  category: '',
  sentimentScore: '',
  extractedEntities: [],
};


const TaskForm = ({ open, handleClose, taskToEdit }) => {
  const userID = useSelector(selectUserID);
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);

  const [formState, setFormState] = useState(initialFormState);
  const [isAnalyzingAI, setIsAnalyzingAI] = useState(false);

  useEffect(() => {
    if (taskToEdit) {
      setFormState({
        title: taskToEdit.title,
        description: taskToEdit.description,
        category: taskToEdit.category || '',
        sentimentScore: taskToEdit.sentimentScore || '',
        extractedEntities: taskToEdit.extractedEntities || [],
      });
    } else {
      setFormState(initialFormState);
    }
  }, [taskToEdit, open]); 

  const performAIAnalysis = useCallback(
    async (title, description) => {
      if (!title && !description) {
        setFormState(prev => ({ ...prev, category: '', sentimentScore: '', extractedEntities: [] }));
        setIsAnalyzingAI(false);
        return;
      }
      setIsAnalyzingAI(true);
      const analysis = await analyzeTaskContent(title, description);
      setFormState(prev => ({
        ...prev,
        category: analysis.category,
        sentimentScore: analysis.sentimentScore,
        extractedEntities: analysis.extractedEntities,
      }));
      setIsAnalyzingAI(false);
    },
    [setFormState, setIsAnalyzingAI]
  );

  const debouncedAnalyzeRef = useRef(debounce(performAIAnalysis, 1000));

  useEffect(() => {
    debouncedAnalyzeRef.current = debounce(performAIAnalysis, 1000);
  }, [performAIAnalysis]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => {
      const newState = { ...prevState, [name]: value };
      if (name === 'title' || name === 'description') {
        debouncedAnalyzeRef.current(newState.title, newState.description);
      }
      return newState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userID) {
      toast.error('You must be logged in to add/edit tasks.');
      return;
    }

    const taskData = {
      ...formState,
      userId: userID,
      firstName: firstName,
      lastName: lastName,
      completed: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      if (taskToEdit) {
        await setDoc(doc(db, 'tasks', taskToEdit.id), taskData);
        toast.success('Task updated successfully!');
      } else {
        await addDoc(collection(db, 'tasks'), taskData);
        toast.success('Task added successfully!');
      }
      handleClose();
      setFormState(initialFormState);
    } catch (error) {
      console.error('Error adding/updating task:', error);
      toast.error('Failed to save task.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{taskToEdit ? 'Edit Task' : 'Add New Task'}</DialogTitle>
      <DialogContent dividers>
        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Task Title"
            type="text"
            fullWidth
            required
            value={formState.title}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={formState.description}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="category-label">Category (AI Generated)</InputLabel>
            <Select
              labelId="category-label"
              id="category"
              name="category"
              value={formState.category}
              label="Category (AI Generated)"
              onChange={handleChange}
              disabled={isAnalyzingAI}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="Work">Work</MenuItem>
              <MenuItem value="Personal">Personal</MenuItem>
              <MenuItem value="Shopping">Shopping</MenuItem>
              <MenuItem value="Health">Health</MenuItem>
              <MenuItem value="Learning">Learning</MenuItem>
              <MenuItem value="Finance">Finance</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="sentimentScore"
            label="Sentiment Score (AI Generated)"
            type="number"
            fullWidth
            value={formState.sentimentScore}
            onChange={handleChange}
            inputProps={{ min: -5, max: 5 }}
            disabled={isAnalyzingAI}
            sx={{ mb: 2 }}
          />
          {formState.extractedEntities && formState.extractedEntities.length > 0 && (
            <TextField
                margin="dense"
                label="Key Entities (AI Generated)"
                fullWidth
                value={formState.extractedEntities.join(', ')}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
            />
          )}

          {isAnalyzingAI && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, mb: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">AI Analyzing...</Typography>
            </Box>
          )}

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={isAnalyzingAI}>
              {taskToEdit ? 'Save Changes' : 'Add Task'}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

function debounce(func, delay) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

export default TaskForm;